import { useState } from 'react';

import { Plus, Scale } from 'lucide-react';
import { toast } from 'sonner';

import {
  AVISO_DE_CADASTRO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { formatarDinheiro } from '@/nucleo/dinheiro';
import { formatarData } from '@/nucleo/formato/datas';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { MoneyInput } from '@/ui/MoneyInput';
import { PageHeader } from '@/ui/PageHeader';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Spinner } from '@/ui/Spinner';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/base/table';

import {
  FLUXOS_DE_APROVACAO,
  ROTULO_DO_FLUXO,
  nomeDaBase,
  useBasesDeCalculo,
  useTiposDeMulta,
  type FluxoDeAprovacao,
} from './dados';

/**
 * `/admin/parametros/tipos-multa` — as infrações puníveis, com o percentual (ou
 * valor fixo) e a base sobre a qual a multa é calculada.
 *
 * "NOVO TIPO" ABRE O FORMULÁRIO, MAS NÃO GRAVA NADA. `/tipos-multa` não existe
 * na API, e a tabela `tipos_multa` (migração `0002_dominio_base.sql`) não tem
 * política de escrita. O botão "Criar" mostra o aviso honesto de
 * indisponibilidade em vez de fingir sucesso — mesmo precedente de
 * `ReajusteAnualPage` (`modulos/operacao/comum/cadastroIndisponivel.ts`).
 *
 * NÃO EXISTE COLUNA DE AÇÕES. O protótipo trazia um botão de excluir por linha;
 * ficou de fora pela regra 7 das regras de interface — o que o usuário não pode
 * fazer não é renderizado, e um botão de apagar que não apaga é o pior caso
 * disso. Volta junto com o endpoint.
 */
export function TiposMultaPage() {
  const { data: tipos, isLoading, isError, error, refetch } = useTiposDeMulta();
  const { data: bases } = useBasesDeCalculo();
  const [novoAberto, setNovoAberto] = useState(false);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tipos de multa"
        description="Infrações puníveis, o percentual ou valor fixo de cada uma e a base de cálculo aplicada."
      >
        <Button onClick={() => setNovoAberto(true)}>
          <Plus className="h-4 w-4 sm:mr-2" aria-hidden="true" />
          <span className="hidden sm:inline">Novo tipo</span>
        </Button>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tipos cadastrados</CardTitle>
          <CardDescription>
            O fluxo de aprovação decide quem confirma a aplicação: automático dispensa análise, os
            demais exigem decisão humana antes de gerar cobrança.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isError ? (
            <ErrorState
              variant="load-failed"
              description={
                error?.aviso ??
                'Não foi possível buscar os tipos de multa agora. Verifique sua conexão e tente novamente.'
              }
              primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
            />
          ) : isLoading ? (
            <Spinner />
          ) : !tipos || tipos.length === 0 ? (
            <EmptyState
              icon={Scale}
              title="Nenhum tipo de multa cadastrado"
              description="Cadastre as infrações para que a fiscalização possa enquadrar ocorrências."
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead className="text-right">Percentual / valor</TableHead>
                    <TableHead>Base de cálculo</TableHead>
                    <TableHead>Fluxo de aprovação</TableHead>
                    <TableHead>Vigência</TableHead>
                    <TableHead>Situação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tipos.map((tipo) => (
                    <TableRow key={tipo.id}>
                      <TableCell className="font-mono text-xs font-medium">{tipo.codigo}</TableCell>
                      <TableCell className="text-sm">{tipo.descricao}</TableCell>
                      <TableCell className="text-right font-mono">
                        {tipo.valor_fixo != null
                          ? formatarDinheiro(tipo.valor_fixo)
                          : `${Number(tipo.percentual ?? 0).toFixed(1)}%`}
                      </TableCell>
                      <TableCell className="text-sm">
                        {nomeDaBase(tipo.base_calculo, bases)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {ROTULO_DO_FLUXO[tipo.fluxo_aprovacao]}
                      </TableCell>
                      <TableCell className="text-sm">
                        {tipo.vigencia_inicio ? formatarData(tipo.vigencia_inicio) : '—'}
                        {tipo.vigencia_fim ? ` até ${formatarData(tipo.vigencia_fim)}` : ''}
                      </TableCell>
                      <TableCell>
                        <Badge variant={tipo.ativo ? 'default' : 'secondary'}>
                          {tipo.ativo ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <NovoTipoDialog open={novoAberto} onOpenChange={setNovoAberto} />
    </div>
  );
}

interface NovoTipoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function NovoTipoDialog({ open, onOpenChange }: NovoTipoDialogProps) {
  const { data: bases } = useBasesDeCalculo();

  const [codigo, setCodigo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [percentual, setPercentual] = useState('5');
  const [valorFixo, setValorFixo] = useState('');
  const [baseCalculo, setBaseCalculo] = useState('VALOR_MENSAL');
  const [fluxo, setFluxo] = useState<FluxoDeAprovacao>('FISCAL');
  const [vigenciaInicio, setVigenciaInicio] = useState('');
  const [vigenciaFim, setVigenciaFim] = useState('');

  /** A base "valor fixo" troca o campo de percentual pelo de dinheiro. */
  const usaValorFixo = baseCalculo === 'VALOR_FIXO';

  function criar() {
    if (!codigo || !descricao) {
      toast.error('Campos obrigatórios', { description: 'Código e descrição são obrigatórios.' });
      return;
    }
    if (usaValorFixo ? !valorFixo : !percentual) {
      toast.error('Campos obrigatórios', {
        description: usaValorFixo
          ? 'Informe o valor fixo da multa.'
          : 'Informe o percentual da multa.',
      });
      return;
    }
    // A tabela `tipos_multa` não aceita escrita hoje — não há rota de criação na
    // API nem política de escrita no banco. O aviso é honesto: nada foi gravado.
    mostrarAviso(AVISO_DE_CADASTRO_INDISPONIVEL);
    onOpenChange(false);
  }

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Novo tipo de multa"
      description="O código identifica a infração no enquadramento feito pela fiscalização."
      desktopMaxWidth="max-w-lg"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={criar}>Criar</Button>
        </>
      }
    >
      <div className="grid gap-3 py-2">
        <div className="grid gap-2">
          <Label htmlFor="multa-codigo">Código</Label>
          <Input
            id="multa-codigo"
            value={codigo}
            onChange={(evento) => setCodigo(evento.target.value.toUpperCase())}
            placeholder="ATRASO_REITERADO"
            className="font-mono"
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="multa-descricao">Descrição</Label>
          <Input
            id="multa-descricao"
            value={descricao}
            onChange={(evento) => setDescricao(evento.target.value)}
            placeholder="O que caracteriza a infração"
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="multa-base">Base de cálculo</Label>
          <Select value={baseCalculo} onValueChange={setBaseCalculo}>
            <SelectTrigger id="multa-base">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(bases ?? []).map((base) => (
                <SelectItem key={base.codigo} value={base.codigo}>
                  {base.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {bases?.find((base) => base.codigo === baseCalculo)?.formula_descritiva && (
            <p className="text-xs text-muted-foreground">
              {bases.find((base) => base.codigo === baseCalculo)?.formula_descritiva}
            </p>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {usaValorFixo ? (
            <div className="grid gap-2">
              <Label htmlFor="multa-valor-fixo">Valor fixo</Label>
              <MoneyInput id="multa-valor-fixo" value={valorFixo} onChange={setValorFixo} />
            </div>
          ) : (
            <div className="grid gap-2">
              <Label htmlFor="multa-percentual">Percentual (%)</Label>
              <Input
                id="multa-percentual"
                type="number"
                step="0.1"
                min={0}
                max={100}
                value={percentual}
                onChange={(evento) => setPercentual(evento.target.value)}
              />
            </div>
          )}
          <div className="grid gap-2">
            <Label htmlFor="multa-fluxo">Fluxo de aprovação</Label>
            <Select value={fluxo} onValueChange={(valor) => setFluxo(valor as FluxoDeAprovacao)}>
              <SelectTrigger id="multa-fluxo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FLUXOS_DE_APROVACAO.map((item) => (
                  <SelectItem key={item} value={item}>
                    {ROTULO_DO_FLUXO[item]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="multa-vigencia-inicio">Vigência inicia em</Label>
            <Input
              id="multa-vigencia-inicio"
              type="date"
              value={vigenciaInicio}
              onChange={(evento) => setVigenciaInicio(evento.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="multa-vigencia-fim">Vigência termina em</Label>
            <Input
              id="multa-vigencia-fim"
              type="date"
              value={vigenciaFim}
              onChange={(evento) => setVigenciaFim(evento.target.value)}
            />
            <p className="text-xs text-muted-foreground">Vazio: vigente por prazo indeterminado.</p>
          </div>
        </div>
      </div>
    </ResponsiveModal>
  );
}

export default TiposMultaPage;
